import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Upload, Trash2, Check, ChevronDown, FileText, Info, Loader2, ShieldCheck, Clock, Save } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { COUNTRIES } from "@/lib/countries";

// Formulario de perfil de la empresa · Fase 1 antes del cuestionario.
const ALLOWED_EXT = "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain";

/**
 * Wizard pre-consultoría con 3 pasos:
 * 1. Datos de la empresa (obligatorio)
 * 2. Información complementaria opcional (redes + docs)
 * 3. Instrucciones del test
 */
export default function ConsultingPreTest() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  // Autosave del wizard en localStorage por si el cliente cierra la pestaña
  const initial = (() => {
    try { return JSON.parse(localStorage.getItem("consulting-pretest-draft") || "{}"); } catch { return {}; }
  })();
  const [companyName, setCompanyName] = useState(initial.companyName || "");
  const [employeesCount, setEmployeesCount] = useState(initial.employeesCount || "");
  const [country, setCountry] = useState(initial.country || "España");
  const [sector, setSector] = useState(initial.sector || "");
  const [sectorOther, setSectorOther] = useState(initial.sectorOther || "");
  const [workModel, setWorkModel] = useState(initial.workModel || "");
  const [teamComposition, setTeamComposition] = useState(initial.teamComposition || "");
  const [website, setWebsite] = useState(initial.website || "");
  const [linkedin, setLinkedin] = useState(initial.linkedin || "");
  const [other, setOther] = useState(initial.other || "");
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [testId, setTestId] = useState(null);

  // Persistir en localStorage cada cambio (draft del wizard antes de crear el test en Mongo)
  useEffect(() => {
    if (testId) return; // ya está en la DB
    try {
      localStorage.setItem(
        "consulting-pretest-draft",
        JSON.stringify({ companyName, employeesCount, country, sector, sectorOther, workModel, teamComposition, website, linkedin, other })
      );
    } catch {}
  }, [companyName, employeesCount, country, sector, sectorOther, workModel, teamComposition, website, linkedin, other, testId]);

  // Limpiar draft cuando ya se ha creado el test en Mongo
  useEffect(() => {
    if (testId) {
      try { localStorage.removeItem("consulting-pretest-draft"); } catch {}
    }
  }, [testId]);

  const totalBytes = documents.reduce((n, d) => n + (d.size || 0), 0);

  async function ensureTestCreated() {
    // Crea el test en cuanto pasamos al paso 2 para poder subir docs
    if (testId) return testId;
    if (!companyName.trim() || !employeesCount || !country.trim()) {
      toast.error("Rellena empresa, empleados y país");
      return null;
    }
    if (!sector) {
      toast.error("Selecciona el sector de tu empresa");
      return null;
    }
    if (sector === "Otro" && !sectorOther.trim()) {
      toast.error('Especifica el sector en el campo "Otro"');
      return null;
    }
    if (!workModel) {
      toast.error("Selecciona el modelo de trabajo");
      return null;
    }
    if (!teamComposition) {
      toast.error("Indica si el equipo incluye personas de distintos países o culturas");
      return null;
    }
    const sectorFinal = sector === "Otro" ? `Otro: ${sectorOther.trim()}`.slice(0, 120) : sector;
    try {
      const r = await api.post("/consulting/tests", {
        company_name: companyName.trim(),
        employees_count: employeesCount,
        country: country.trim(),
        sector: sectorFinal,
        work_model: workModel,
        team_composition: teamComposition,
        social_links: { website, linkedin, other },
      });
      setTestId(r.data.id);
      return r.data.id;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el test");
      return null;
    }
  }

  async function goToStep2() {
    const id = await ensureTestCreated();
    if (id) setStep(2);
  }

  async function goToStep3() {
    if (testId) {
      try {
        await api.patch(`/consulting/tests/${testId}/social_links`, {
          social_links: { website, linkedin, other },
        });
      } catch {}
    }
    setStep(3);
  }

  async function handleFileUpload(evt) {
    const files = Array.from(evt.target.files || []);
    if (!files.length || !testId) return;
    if (documents.length + files.length > 5) {
      toast.error("Máximo 5 archivos por test");
      return;
    }
    setUploading(true);
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`"${file.name}" supera 5 MB`);
        continue;
      }
      if (totalBytes + file.size > 10 * 1024 * 1024) {
        toast.error("Superarías 10 MB totales");
        break;
      }
      const form = new FormData();
      form.append("file", file);
      try {
        const res = await fetch(`${API_BASE}/consulting/tests/${testId}/documents`, {
          method: "POST",
          headers: { Authorization: `Bearer ${getToken()}` },
          body: form,
        });
        const j = await res.json();
        if (!res.ok) {
          toast.error(j.detail || `No pudimos subir ${file.name}`);
          continue;
        }
        setDocuments((prev) => [...prev, j.document]);
      } catch {
        toast.error(`Error subiendo ${file.name}`);
      }
    }
    setUploading(false);
    evt.target.value = "";
  }

  async function removeDocument(docId) {
    if (!testId) return;
    try {
      await api.delete(`/consulting/tests/${testId}/documents/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch {
      toast.error("No pudimos borrar");
    }
  }

  async function startTest() {
    if (!testId) return;
    setSubmitting(true);
    navigate(`/consulting/test/${testId}`);
  }

  const progressPct = Math.round((step / 3) * 100);

  return (
    <div
      className="min-h-screen px-6 py-12 flex items-start justify-center"
      style={{
        background:
          "radial-gradient(1200px 500px at 100% -10%, rgba(15,110,86,0.06) 0%, rgba(15,110,86,0) 60%)," +
          "radial-gradient(900px 400px at -10% 20%, rgba(201,168,76,0.06) 0%, rgba(201,168,76,0) 55%)," +
          "linear-gradient(180deg, #fafaf7 0%, #f5f5f1 100%)",
      }}
    >
      <div className="max-w-2xl w-full">
        <button
          onClick={() => navigate("/consulting")}
          className="text-sm font-semibold text-slate-500 hover:text-[#0F6E56] inline-flex items-center gap-1.5 mb-4 md:mb-6 transition-colors group"
          data-testid="pretest-back-btn"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" /> Volver a mis consultorías
        </button>
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mb-3">
            <div className="inline-flex self-start items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold px-3 py-1 rounded-full max-w-full" style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)", color: "#E5C874", boxShadow: "0 4px 12px -4px rgba(11,61,46,0.35)" }}>
              <span style={{ width: 5, height: 5, borderRadius: 999, background: "#C9A84C", display: "inline-block", flexShrink: 0 }} />
              <span className="truncate">Consultoría Premium · {step}/3</span>
            </div>
            <div className="text-xs font-semibold self-end sm:self-auto" style={{ color: "#0F6E56" }}>{progressPct}%</div>
          </div>
          <div className="h-2 rounded-full overflow-hidden mb-3" style={{ background: "rgba(11,61,46,0.08)" }}>
            <div className="h-full transition-all duration-500" style={{ width: `${progressPct}%`, background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 55%, #C9A84C 100%)", boxShadow: "0 0 10px rgba(201,168,76,0.55)" }} />
          </div>
          {/* Preview de los 3 pasos con nombres */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { n: 1, label: "Empresa" },
              { n: 2, label: "Contexto" },
              { n: 3, label: "Instrucciones" },
            ].map((s) => {
              const done = step > s.n;
              const active = step === s.n;
              return (
                <div
                  key={s.n}
                  className="rounded-lg px-2 py-1.5 transition-all"
                  style={{
                    background: active ? "linear-gradient(135deg, rgba(15,110,86,0.10) 0%, rgba(201,168,76,0.10) 100%)" : done ? "rgba(15,110,86,0.06)" : "transparent",
                    border: `1px solid ${active ? "rgba(15,110,86,0.35)" : done ? "rgba(15,110,86,0.15)" : "rgba(11,61,46,0.06)"}`,
                  }}
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      className="w-4 h-4 rounded-full text-[9px] font-bold hidden min-[380px]:flex items-center justify-center flex-shrink-0"
                      style={{
                        background: done ? "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)" : active ? "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" : "#e2e8f0",
                        color: done ? "#0b3d2e" : active ? "#E5C874" : "#94a3b8",
                      }}
                    >
                      {done ? "✓" : s.n}
                    </span>
                    <span className="text-[10px] sm:text-[11px] font-semibold truncate" style={{ color: active ? "#0b3d2e" : done ? "#0F6E56" : "#94a3b8" }}>{s.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div
          className="rounded-3xl p-8 md:p-10 relative overflow-hidden"
          style={{ background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)", boxShadow: "0 22px 45px -30px rgba(11,61,46,0.35), 0 0 0 1px rgba(11,61,46,0.06), inset 0 1px 0 rgba(255,255,255,0.9)" }}
          data-testid="consulting-pretest"
        >
          <span aria-hidden className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: "linear-gradient(90deg, #0b3d2e 0%, #0F6E56 45%, #C9A84C 100%)" }} />
          {/* PASO 1: Datos empresa */}
          {step === 1 && (
            <>
              <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Paso 1 · Cuéntanos sobre tu empresa</h1>
              <p className="text-sm text-slate-600 mb-8 leading-relaxed">
                Necesitamos cinco datos básicos sobre tu empresa para personalizar el análisis y las recomendaciones.
              </p>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Nombre de la empresa</label>
                  <input type="text" placeholder="Ej. Talent SL" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-emerald-600 focus:outline-none" value={companyName} onChange={(e) => setCompanyName(e.target.value)} data-testid="pretest-company-input" maxLength={120} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Número exacto de empleados</label>
                  <input
                    type="number"
                    min="1"
                    max="100000"
                    step="1"
                    inputMode="numeric"
                    pattern="\d+"
                    placeholder="Ej. 32"
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-emerald-600 focus:outline-none"
                    value={employeesCount}
                    onChange={(e) => {
                      // Sanitización defensiva: NO concatenamos dígitos de
                      // rangos ("11-50" NO debe convertirse en "1150", eso
                      // dispararía recomendaciones para una enterprise de
                      // 1150 personas en vez de una pyme). Nos quedamos SOLO
                      // con el primer bloque contiguo de dígitos y avisamos
                      // al usuario cuando descartamos algo.
                      const raw = e.target.value || "";
                      const match = raw.match(/^\s*(\d{1,6})/);
                      const digitsOnly = match ? match[1] : "";
                      const hadNonDigit = /[^\d\s]/.test(raw);
                      if (hadNonDigit && digitsOnly && raw.trim() !== digitsOnly) {
                        toast.info(
                          `Hemos interpretado "${raw.trim()}" como ${digitsOnly}. Si querías dar un rango, introduce el número exacto.`,
                          { duration: 6000 }
                        );
                      }
                      setEmployeesCount(digitsOnly);
                    }}
                    onBlur={() => {
                      // Sanity checks sin bloquear (el usuario puede corregir).
                      const n = parseInt(employeesCount, 10);
                      if (!employeesCount) return;
                      if (!Number.isFinite(n) || n < 1) {
                        toast.error("El número de empleados debe ser al menos 1.");
                        setEmployeesCount("");
                      } else if (n > 50000) {
                        toast.warning(
                          `Has introducido ${n} empleados. Si es correcto, ignora este aviso.`,
                          { duration: 5000 }
                        );
                      }
                    }}
                    data-testid="pretest-employees-input"
                  />
                  <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
                    Introduce el <strong>headcount actual exacto</strong>. Esta cifra se usa para dimensionar recomendaciones (contrataciones, ratios).
                  </p>
                  <p className="mt-1.5 text-[11px] text-slate-500 leading-relaxed">
                    Si das un rango, el informe usará lenguaje aproximado.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">País</label>
                  <CountryCombobox value={country} onChange={setCountry} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    ¿En qué sector opera tu empresa?
                  </label>
                  <select
                    value={sector}
                    onChange={(e) => {
                      setSector(e.target.value);
                      if (e.target.value !== "Otro") setSectorOther("");
                    }}
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20 transition"
                    data-testid="pretest-sector-select"
                  >
                    <option value="" disabled>Selecciona un sector…</option>
                    <option value="SaaS / Software">SaaS / Software</option>
                    <option value="Fintech / Servicios financieros digitales">Fintech / Servicios financieros digitales</option>
                    <option value="Healthtech / Salud digital">Healthtech / Salud digital</option>
                    <option value="Edtech / Formación digital">Edtech / Formación digital</option>
                    <option value="E-commerce / Comercio digital">E-commerce / Comercio digital</option>
                    <option value="Agencia digital / Marketing">Agencia digital / Marketing</option>
                    <option value="IA / Datos / Analytics">IA / Datos / Analytics</option>
                    <option value="Hardware / Deep tech">Hardware / Deep tech</option>
                    <option value="Otro">Otro (especificar)</option>
                  </select>
                  {sector === "Otro" && (
                    <input
                      type="text"
                      value={sectorOther}
                      onChange={(e) => setSectorOther(e.target.value)}
                      placeholder="Especifica tu sector"
                      maxLength={80}
                      className="mt-2 w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20 transition"
                      data-testid="pretest-sector-other-input"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    ¿Cuál es el modelo de trabajo de tu empresa?
                  </label>
                  <div
                    role="radiogroup"
                    aria-label="Modelo de trabajo"
                    className="grid grid-cols-1 sm:grid-cols-3 gap-2"
                    data-testid="pretest-workmodel-group"
                  >
                    {[
                      { value: "100% presencial", label: "100% presencial" },
                      { value: "Modelo híbrido", label: "Modelo híbrido" },
                      { value: "100% remoto", label: "100% remoto" },
                    ].map((opt) => {
                      const active = workModel === opt.value;
                      return (
                        <button
                          type="button"
                          key={opt.value}
                          role="radio"
                          aria-checked={active}
                          onClick={() => setWorkModel(opt.value)}
                          className={`rounded-lg border px-3 py-3 text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-emerald-600/20 ${
                            active
                              ? "border-emerald-600 bg-emerald-50 text-emerald-800 shadow-inner"
                              : "border-slate-300 bg-white text-slate-700 hover:border-emerald-400 hover:bg-emerald-50/40"
                          }`}
                          data-testid={`pretest-workmodel-${opt.value.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-btn`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    ¿Tu equipo incluye personas de distintos países o culturas?
                  </label>
                  <div
                    role="radiogroup"
                    aria-label="Composición del equipo"
                    className="grid grid-cols-1 gap-2"
                    data-testid="pretest-team-composition-group"
                  >
                    {[
                      { value: "mono_cultural", label: "No, todo el equipo comparte país y cultura" },
                      { value: "some_intl", label: "Sí, algunas personas" },
                      { value: "mostly_intl", label: "Sí, es un equipo mayoritariamente internacional" },
                    ].map((opt) => {
                      const active = teamComposition === opt.value;
                      return (
                        <button
                          type="button"
                          key={opt.value}
                          role="radio"
                          aria-checked={active}
                          onClick={() => setTeamComposition(opt.value)}
                          className={`rounded-lg border px-3 py-3 text-sm font-medium text-left transition-all focus:outline-none focus:ring-2 focus:ring-emerald-600/20 ${
                            active
                              ? "border-emerald-600 bg-emerald-50 text-emerald-800 shadow-inner"
                              : "border-slate-300 bg-white text-slate-700 hover:border-emerald-400 hover:bg-emerald-50/40"
                          }`}
                          data-testid={`pretest-team-composition-${opt.value}-btn`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-1.5 text-[11px] text-slate-500 leading-relaxed">
                    Si tu equipo es mixto, añadimos preguntas específicas sobre cómo se acuerda la comunicación entre culturas para que el informe sea más útil.
                  </p>
                </div>
              </div>
              <button
                onClick={goToStep2}
                disabled={!companyName.trim() || !employeesCount || !country.trim() || !sector || (sector === "Otro" && !sectorOther.trim()) || !workModel || !teamComposition}
                className="group relative mt-8 w-full rounded-full py-3.5 font-bold inline-flex items-center justify-center gap-2 overflow-hidden transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/60"
                style={{
                  background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 55%, #0b3d2e 100%)",
                  color: "#E5C874",
                  border: "1px solid rgba(201,168,76,0.35)",
                  boxShadow: "0 10px 24px -12px rgba(11,61,46,0.55), 0 2px 6px -2px rgba(11,61,46,0.35), inset 0 1px 0 rgba(229,200,116,0.18)",
                  letterSpacing: "0.02em",
                }}
                data-testid="pretest-step1-next-btn"
              >
                <span aria-hidden className="absolute inset-x-6 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(229,200,116,0.55) 50%, transparent 100%)" }} />
                <span className="relative z-10 inline-flex items-center gap-2">
                  Continuar
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </span>
              </button>
            </>
          )}

          {/* PASO 2: Info complementaria */}
          {step === 2 && (
            <>
              <div className="flex items-start gap-3 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full mt-1" style={{ background: "#fef3c7", color: "#92400e" }}>Opcional</span>
                <h1 className="font-display text-2xl font-bold text-slate-900">Paso 2 · Ayúdanos a personalizar tu análisis</h1>
              </div>
              <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                Cuanto más contexto nos des, más específico será tu informe. <strong>Todo es opcional</strong>: puedes saltar directamente al test si prefieres.
              </p>
              <div className="space-y-5" data-testid="pretest-complementary-section">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Web de la empresa</label>
                  <input type="url" placeholder="https://tuempresa.com" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-emerald-600 focus:outline-none" value={website} onChange={(e) => setWebsite(e.target.value)} data-testid="pretest-website-input" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">LinkedIn de la empresa</label>
                  <input type="url" placeholder="https://linkedin.com/company/…" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-emerald-600 focus:outline-none" value={linkedin} onChange={(e) => setLinkedin(e.target.value)} data-testid="pretest-linkedin-input" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Otra URL relevante</label>
                  <input type="url" placeholder="Blog, Notion, careers page…" className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-emerald-600 focus:outline-none" value={other} onChange={(e) => setOther(e.target.value)} data-testid="pretest-other-input" />
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Documentos internos</label>
                  <p className="text-xs text-slate-500 mb-3">PDF, Word o TXT · máx 5 archivos · 5 MB c/u · 10 MB en total.</p>
                  <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg border-2 border-dashed border-emerald-300 px-4 py-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 transition" data-testid="pretest-upload-label">
                    <Upload className="w-4 h-4" />
                    {uploading ? "Subiendo…" : "Añadir documento"}
                    <input type="file" accept={ALLOWED_EXT} multiple onChange={handleFileUpload} className="hidden" data-testid="pretest-upload-input" disabled={uploading || documents.length >= 5} />
                  </label>
                  {documents.length > 0 && (
                    <ul className="mt-4 space-y-2" data-testid="pretest-documents-list">
                      {documents.map((d) => (
                        <li key={d.id} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)", boxShadow: "0 0 0 1px rgba(11,61,46,0.08)" }}>
                          <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="truncate flex-1 text-slate-700">{d.original_filename}</span>
                          <span className="text-xs text-slate-400 flex-shrink-0">{Math.max(1, Math.round(d.size / 1024))} KB</span>
                          <button onClick={() => removeDocument(d.id)} className="text-slate-400 hover:text-rose-600 flex-shrink-0" aria-label="Eliminar" data-testid={`pretest-doc-remove-${d.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="text-[11px] text-slate-500 mt-2">
                    {documents.length}/5 archivos · {(totalBytes / (1024 * 1024)).toFixed(2)}/10 MB
                  </div>
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={() => setStep(1)} className="rounded-full px-5 py-3 text-sm font-semibold text-slate-600 border border-slate-300 inline-flex items-center gap-1.5" data-testid="pretest-step2-back-btn">
                  <ArrowLeft className="w-4 h-4" /> Atrás
                </button>
                <button
                  onClick={goToStep3}
                  className="group relative flex-1 rounded-full py-3.5 font-bold inline-flex items-center justify-center gap-2 overflow-hidden transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/60"
                  style={{
                    background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 55%, #0b3d2e 100%)",
                    color: "#E5C874",
                    border: "1px solid rgba(201,168,76,0.35)",
                    boxShadow: "0 10px 24px -12px rgba(11,61,46,0.55), 0 2px 6px -2px rgba(11,61,46,0.35), inset 0 1px 0 rgba(229,200,116,0.18)",
                    letterSpacing: "0.02em",
                  }}
                  data-testid="pretest-step2-next-btn"
                >
                  <span aria-hidden className="absolute inset-x-6 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(229,200,116,0.55) 50%, transparent 100%)" }} />
                  <span className="relative z-10 inline-flex items-center gap-2">
                    Continuar
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </span>
                </button>
              </div>
            </>
          )}

          {/* PASO 3: Instrucciones del test */}
          {step === 3 && (
            <>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#ecfdf5" }}>
                  <Info className="w-5 h-5" style={{ color: "#0F6E56" }} />
                </div>
                <h1 className="font-display text-2xl font-bold text-slate-900">Paso 3 · Antes de empezar el cuestionario</h1>
              </div>
              <p className="text-sm text-slate-600 mb-6 leading-relaxed">
                Antes de empezar, es importante que sepas cómo aprovechar al máximo esta consultoría. Léelo con calma — solo son 3 minutos.
              </p>
              <div className="space-y-4" data-testid="pretest-instructions-section">
                <InstructionRow icon={<FileText className="w-5 h-5" />} title="35 preguntas en 6 áreas (37 si tu equipo es multicultural)">
                  Marca empleadora, selección, onboarding, evaluación del desempeño, feedback y desarrollo. Cada respuesta alimenta un análisis específico de esa área en tu informe.
                </InstructionRow>
                <InstructionRow icon={<Clock className="w-5 h-5" />} title="35–45 minutos de dedicación (orientativo)">
                  Tómatelo con calma, no es una encuesta. Puedes salir y volver cuando quieras: guardamos automáticamente cada respuesta.
                </InstructionRow>
                <InstructionRow icon={<Save className="w-5 h-5" />} title="Autoguardado cada 3 segundos">
                  Puedes cerrar la pestaña sin miedo. Cuando vuelvas, verás tu borrador en <strong>Tus consultorías</strong> con la opción "Continuar".
                </InstructionRow>
                <InstructionRow icon={<ShieldCheck className="w-5 h-5" />} title="No es un examen, es un espejo">
                  <p><strong>Cuanto más honestas sean tus respuestas, más útil será el informe.</strong> Nadie más verá tus respuestas individuales.</p>
                  <p>Responder lo que "debería ser" produce un análisis genérico; responder con crudeza produce un plan accionable para tu realidad. Nuestro sistema detecta patrones optimistas homogéneos y los contrasta con datos del mercado de tu país para señalar puntos ciegos, no para juzgarte.</p>
                </InstructionRow>
                <InstructionRow icon={<Check className="w-5 h-5" />} title="Al terminar">
                  <p>Tu informe se genera automáticamente y estará listo en 24 horas.</p>
                  <p>Recibirás un email cuando esté disponible: análisis personalizado en PDF, checklist interactivo y 7 recursos operativos ajustados a tu contexto.</p>
                </InstructionRow>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={() => setStep(2)} className="rounded-full px-5 py-3 text-sm font-semibold text-slate-600 border border-slate-300 inline-flex items-center gap-1.5" data-testid="pretest-step3-back-btn">
                  <ArrowLeft className="w-4 h-4" /> Atrás
                </button>
                <button
                  onClick={startTest}
                  disabled={submitting}
                  className="group relative flex-1 rounded-full py-3.5 font-bold inline-flex items-center justify-center gap-2 overflow-hidden transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/60"
                  style={{
                    background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 55%, #0b3d2e 100%)",
                    color: "#E5C874",
                    border: "1px solid rgba(201,168,76,0.35)",
                    boxShadow: "0 10px 24px -12px rgba(11,61,46,0.55), 0 2px 6px -2px rgba(11,61,46,0.35), inset 0 1px 0 rgba(229,200,116,0.18)",
                    letterSpacing: "0.02em",
                  }}
                  data-testid="pretest-start-test-btn"
                >
                  <span aria-hidden className="absolute inset-x-6 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent 0%, rgba(229,200,116,0.55) 50%, transparent 100%)" }} />
                  <span className="relative z-10 inline-flex items-center gap-2">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />}
                    <span className="sm:hidden">Empezar</span>
                    <span className="hidden sm:inline">Empezar el cuestionario</span>
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InstructionRow({ icon, title, children }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl relative overflow-hidden" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fdfcf6 100%)", boxShadow: "0 4px 12px -6px rgba(11,61,46,0.12), 0 0 0 1px rgba(11,61,46,0.06)" }}>
      <span aria-hidden className="absolute top-0 bottom-0 left-0 w-1" style={{ background: "linear-gradient(180deg, #C9A84C 0%, #E5C874 100%)" }} />
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#0F6E56", color: "#C9A84C" }}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-slate-900 text-sm mb-0.5">{title}</div>
        <div className="text-xs text-slate-600 leading-relaxed space-y-1.5">{children}</div>
      </div>
    </div>
  );
}

/**
 * Combobox de país con búsqueda tipo Command palette (~250 países).
 * - Muestra bandera emoji + nombre en español.
 * - `value` es el nombre en español (mantiene compatibilidad con el resto del flujo).
 * - Filtra por nombre acento-insensitive.
 */
function CountryCombobox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = COUNTRIES.find((c) => c.name === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="w-full inline-flex items-center justify-between rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20 transition"
          data-testid="pretest-country-input"
        >
          <span className="inline-flex items-center gap-2 truncate">
            {selected ? (
              <>
                <FlagImg country={selected} />
                <span className="truncate">{selected.name}</span>
              </>
            ) : (
              <span className="text-slate-400">Selecciona tu país…</span>
            )}
          </span>
          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={2} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]"
        align="start"
        sideOffset={4}
      >
        <Command
          filter={(itemValue, search) => {
            // itemValue es el name (lowercased por cmdk). Comparación acento-insensitive.
            const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
            return norm(itemValue).includes(norm(search)) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Buscar país…" className="h-10" data-testid="pretest-country-search" />
          <CommandList className="max-h-72">
            <CommandEmpty>Ningún país coincide.</CommandEmpty>
            <CommandGroup>
              {COUNTRIES.map((c) => (
                <CommandItem
                  key={c.code}
                  value={c.name}
                  onSelect={(v) => {
                    onChange(v);
                    setOpen(false);
                  }}
                  className="text-sm"
                  data-testid={`pretest-country-option-${c.code.toLowerCase()}`}
                >
                  <FlagImg country={c} className="mr-2" />
                  <span className="flex-1 truncate">{c.name}</span>
                  {selected?.code === c.code && <Check className="w-4 h-4 text-emerald-600" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


/**
 * Renderiza la bandera de un país como <img> desde flagcdn.com.
 * Se usa `<img>` en lugar de emoji porque Windows Chrome/Edge no incluye
 * banderas emoji en su fuente Segoe UI Emoji y muestra las siglas ISO como texto.
 * Con flagcdn.com el resultado es consistente en Windows, macOS, Linux, iOS y Android.
 */
function FlagImg({ country, className = "" }) {
  return (
    <img
      src={country.flagUrl}
      srcSet={`${country.flagUrl} 1x, ${country.flagUrl2x} 2x`}
      alt=""
      aria-hidden="true"
      width={20}
      height={15}
      loading="lazy"
      decoding="async"
      className={`inline-block w-5 h-[15px] rounded-[2px] object-cover shrink-0 ring-1 ring-slate-200/60 ${className}`}
    />
  );
}

